import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";

import {
    DffFormControlType,
    DffOprindeligAktuelVaerdiFormGroup,
    DffValidators,
    DffReactiveFormControlValueMapper as Map
} from "@dffedb/forms";
import { formatNumber, Pattern } from "@dffedb/util";
import { NoteFormBuilder } from "@e-forsyning/common/note";

import { MaalerAflaesningMedNoter } from "../model/maaler-aflaesning-med-noter.model";
import { MaalerAflaesning } from "../model/maaler-aflaesning.model";
import { MaalerType } from "../model/maaler-type.model";
import { TaellevaerkAflaesning } from "../model/taellevaerk-aflaesning.model";
import { TaellevaerkType } from "../model/taellevaerk-type.model";

export class MaalerAflaesningFormBuilder {
    public static maalerAflaesningToFormGroup(
        maalerAflaesning: MaalerAflaesning,
        maalerType: MaalerType,
        visTidligereAflaesninger: boolean
    ): UntypedFormGroup {
        const result = new UntypedFormGroup({
            maalerNr: new DffOprindeligAktuelVaerdiFormGroup(maalerAflaesning.maalerNr),
            maalerTypeId: new DffOprindeligAktuelVaerdiFormGroup(maalerAflaesning.maalerTypeId, { hidden: false }, [
                Validators.minLength(1)
            ]),
            taellevaerker: this.taellevaerkAflaesningerToFormArray(
                maalerAflaesning.taellevaerker,
                maalerType.taellevaerker,
                visTidligereAflaesninger
            )
        });
        return result;
    }

    public static taellevaerkAflaesningerToFormArray(
        taellevaerkAflaesninger: TaellevaerkAflaesning[],
        taellevaerkTyper: TaellevaerkType[],
        visTidligereAflaesninger: boolean
    ): UntypedFormArray {
        const result = taellevaerkAflaesninger.map((taellevaerkAflaesning: any) => {
            const index = taellevaerkAflaesninger.indexOf(taellevaerkAflaesning);
            const taellevaerkType = taellevaerkTyper[index];
            return this.taellevaerkAflaesningToFormGroup(taellevaerkAflaesning, taellevaerkType, visTidligereAflaesninger);
        });
        return new UntypedFormArray(result);
    }

    public static formGroupToMaalerAflaesningMedNoter(formGroup: UntypedFormGroup): MaalerAflaesningMedNoter {
        const maalerAflaesning = this.formGroupToMaalerAflaesning(formGroup);
        const noter = NoteFormBuilder.formArrayToNoter(formGroup.get("noter") as UntypedFormArray);
        const result: MaalerAflaesningMedNoter = {
            ...maalerAflaesning,
            noter
        };
        return result;
    }

    public static formGroupToMaalerAflaesning(formGroup: UntypedFormGroup): MaalerAflaesning {
        const result: MaalerAflaesning = {
            maalerNr: Map.toOprindeligAktuelStrings(formGroup.controls["maalerNr"]),
            maalerTypeId: Map.toOprindeligAktuelIntegers(formGroup.controls["maalerTypeId"]),
            taellevaerker: this.formArrayToTaellevaerkAflaesninger(formGroup.controls["taellevaerker"] as UntypedFormArray)
        };
        return result;
    }

    private static taellevaerkAflaesningToFormGroup(
        aflaesning: TaellevaerkAflaesning,
        taellevaerkType: TaellevaerkType,
        visTidligereAflaesning: boolean
    ): UntypedFormGroup {
        const historiskAflaesningFormGroup = new UntypedFormControl(aflaesning.historiskAflaesning);
        const aflaesningFormGroup = new DffOprindeligAktuelVaerdiFormGroup(
            aflaesning.aflaesning,
            {
                controlType: DffFormControlType.NumberInput,
                hint: visTidligereAflaesning
                    ? `<sup>*</sup>${formatNumber(aflaesning.historiskAflaesning)} ${taellevaerkType && taellevaerkType.enhed}`
                    : "",
                placeholder: (taellevaerkType && taellevaerkType.beskrivelse) || "Værdi",
                suffix: taellevaerkType && taellevaerkType.enhed
            },
            [
                Validators.required,
                Validators.pattern(Pattern.positivKommaTal),
                DffValidators.decimals(taellevaerkType && taellevaerkType.decimaler)
            ]
        );
        const result = new UntypedFormGroup({
            aflaesning: aflaesningFormGroup,
            historiskAflaesning: historiskAflaesningFormGroup,
            enhedType: new UntypedFormControl(aflaesning.enhedType)
        });
        return result;
    }

    private static formArrayToTaellevaerkAflaesninger(formArray: UntypedFormArray): TaellevaerkAflaesning[] {
        const result = formArray.controls.map((c) => this.formGroupToTaellevaerkAflaesning(c as UntypedFormGroup));
        return result;
    }

    private static formGroupToTaellevaerkAflaesning(formGroup: UntypedFormGroup): TaellevaerkAflaesning {
        const result: TaellevaerkAflaesning = {
            aflaesning: Map.toOprindeligAktuelFloats(formGroup.controls["aflaesning"], null),
            historiskAflaesning: Map.toFloat(formGroup.controls["historiskAflaesning"], null),
            enhedType: Map.toString(formGroup.controls["enhedType"])
        };
        return result;
    }
}
